import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import BlockImageOsd from "./block-image-osd";
import {
  stepBlockImageAction,
  closeBlockImageAction,
  setSyncPointModeAction,
  addSyncLandmarkAction,
  undoSyncLandmarkAction,
  clearSyncLandmarksAction,
  applyLandmarkAlignmentAction,
  adjustSyncPointsAction,
} from "../../actions/viewer-action";
import { DEFAULT_BLOCK_IMAGE_SYNC } from "../../../imageViewer/reducers/viewer-reducer";
import viewerSync from "../../utils/viewer-sync";
import {
  alignmentQuality,
  getSectionStarMpp,
  MIN_LANDMARK_PAIRS,
} from "../../utils/viewer-sync-math";
import "./block-images.scss";

// Landmark alignment (phase1-prototype-plan.md step 7): click the same feature in
// the slide and in the block image, at least MIN_LANDMARK_PAIRS times, then Apply.
// The fit sets the block image's rotation and position, and checks the zoom: the
// nominal µm/px is kept unless the points show it is completely different. The Sync
// button in the toolbar then locks it.
// Fit error and scale check combined into one status (viewer-sync-math.js
// alignmentQuality), so the user sees a colour rather than numbers.
const QUALITY_LABELS = { good: "Good", fair: "Fair", poor: "Poor" };

const AlignmentQuality = ({ sync }) => {
  if (sync.transformSource !== "landmarks") return null;
  const quality = alignmentQuality({
    residualUm: sync.residualUm,
    scaleRatio: sync.scaleCheck ? sync.scaleCheck.ratio : null,
  });
  return (
    <span className={`block-image-align-quality ${quality}`}>
      <span className="dot" />
      Alignment quality: {QUALITY_LABELS[quality]}
    </span>
  );
};

AlignmentQuality.propTypes = {
  sync: PropTypes.object.isRequired,
};

// Sync workflow, driven by the toolbar sync button:
//   1. Before sync the two panes are independent; this bar only says how to start.
//   2. The sync button opens a guided setup: pick 3+ matching pairs of points, as
//      far apart as possible.
//   3. "Apply & sync" fits the transform and turns sync on immediately.
// After that the points are kept but hidden; hovering "Adjust points" shows them, and
// clicking it unsyncs and goes back to them, so re-aligning needs no new points.
const AlignBar = ({ sync, adjust, cancel, setHover, undo, clear, apply }) => {
  const { wsi, ss } = sync.landmarks;
  const hasPoints = wsi.length > 0 || ss.length > 0;
  const pairs = Math.min(wsi.length, ss.length);

  if (sync.synced) {
    return (
      <div className="block-image-align-bar">
        <span className="block-image-align-status">Synced · rotation locked</span>
        <AlignmentQuality sync={sync} />
        <button
          type="button"
          onClick={adjust}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          title="Hover to see the points; click to unsync and adjust them"
        >
          Adjust points
        </button>
      </div>
    );
  }

  if (!sync.pointMode) {
    return (
      <div className="block-image-align-bar">
        <span className="block-image-align-status">
          To sync with the slide, click the sync button in the toolbar.
          {hasPoints ? " Your previous points are kept." : ""}
        </span>
      </div>
    );
  }

  const nextStep =
    wsi.length > ss.length
      ? `Pair ${wsi.length}: now click the same feature on the block image (right).`
      : `Pair ${wsi.length + 1}: click a feature on the slide (left).`;

  return (
    <div className="block-image-align-bar setup">
      <div className="block-image-align-guide">
        <strong>Set up sync: pick 3 matching points.</strong> Click a feature on the
        slide, then the same feature on the block image. Choose points as far apart as
        possible, near the edges of the tissue and not in a line; points close
        together make the edges drift.
      </div>
      <span className="block-image-align-status">
        {pairs}/{MIN_LANDMARK_PAIRS} pairs · {nextStep}
      </span>
      <button type="button" onClick={undo} disabled={!hasPoints}>
        Undo
      </button>
      <button type="button" onClick={clear} disabled={!hasPoints}>
        Clear
      </button>
      <button type="button" onClick={cancel}>
        Cancel
      </button>
      <button
        type="button"
        className="primary"
        onClick={apply}
        disabled={pairs < MIN_LANDMARK_PAIRS}
        title={
          pairs < MIN_LANDMARK_PAIRS
            ? "Place at least 3 pairs of matching points"
            : "Align the block image to the points and start sync"
        }
      >
        Apply &amp; sync
      </button>
    </div>
  );
};

AlignBar.propTypes = {
  sync: PropTypes.object.isRequired,
  adjust: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired,
  setHover: PropTypes.func.isRequired,
  undo: PropTypes.func.isRequired,
  clear: PropTypes.func.isRequired,
  apply: PropTypes.func.isRequired,
};

// Fills .viewer-pane-right, the right half of the split below the purple patient bar.
// It is docked, not floating, so it covers nothing: the Slide Manager sidebar stays
// clickable — which the two-way selection sync depends on — without any deliberate
// avoidance of a backdrop. The pane owns the panel's width and height; see
// .viewer-split-row in viewer-container.scss.
//
// The panel owns no selection state. It reads selectedBlockImageIndex and dispatches
// changes to it, exactly as the sidebar list does — which is what keeps the two in
// step without any explicit synchronisation.
// Stable identity, so connect()'s shallow compare does not see a new array each time.
const EMPTY_BLOCK_IMAGES = [];

const BlockImagePanel = ({
  images,
  loading,
  selectedIndex,
  stepImage,
  close,
  sync,
  setPointMode,
  adjustPoints,
  addLandmark,
  undoLandmark,
  clearLandmarks,
  applyLandmarks,
}) => {
  const isOpen = selectedIndex !== null && selectedIndex !== undefined;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      // OpenSeadragon's full-screen mode exits on Escape too. Let the browser have
      // that first press rather than closing the panel out from under it.
      if (document.fullscreenElement) {
        return;
      }
      // While placing points, Escape leaves point mode instead of closing.
      if (sync.pointMode) {
        setPointMode(false);
        return;
      }
      close();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close, sync.pointMode, setPointMode]);

  // Clicks in either viewer become landmark points while point mode is on.
  useEffect(() => {
    if (!sync.pointMode) {
      return undefined;
    }
    viewerSync.setPointHandler(addLandmark);
    return () => viewerSync.setPointHandler(null);
  }, [sync.pointMode, addLandmark]);

  // Markers show only while placing points or hovering "Adjust points".
  const [hoverAlign, setHoverAlign] = useState(false);
  const showMarkers = sync.pointMode || hoverAlign;
  useEffect(() => {
    viewerSync.setLandmarksVisible(showMarkers);
  }, [showMarkers]);
  useEffect(() => () => viewerSync.setLandmarksVisible(false), []);

  if (!isOpen) {
    return null;
  }

  const current = images[selectedIndex];

  // Defensive: an index can outlive the list it pointed into if a refetch returns
  // fewer images than before.
  if (!current && !loading) {
    return null;
  }

  return (
    <div className="block-image-panel">
      {/* Thin header strip naming the current image, with the close button anchored
          to its right edge. With up to six images in the carousel instead of three,
          the panel needs to say which one is showing — this replaces the earlier
          chrome-less design, where the close button floated over the top of the
          right-hand gutter with no caption at all. */}
      <div className="block-image-panel-caption">
        <span className="block-image-panel-caption-text">
          {current ? `${current.stateLabel} — ${current.typeLabel}` : ""}
        </span>
        <button
          type="button"
          className="block-image-panel-close"
          onClick={close}
          title="Close"
          aria-label="Close block images"
        >
          &#10005;
        </button>
      </div>

      {!loading && current && (
        <AlignBar
          sync={sync}
          adjust={() => {
            setHoverAlign(false);
            adjustPoints();
          }}
          cancel={() => setPointMode(false)}
          setHover={setHoverAlign}
          undo={undoLandmark}
          clear={clearLandmarks}
          apply={applyLandmarks}
        />
      )}

      {/* Row layout: the arrows are gutter columns either side of the image, not
          overlays on top of it. Hidden for a single image — arrows that only ever
          return you to where you already are read as broken. */}
      <div className="block-image-panel-body">
        {!loading && images.length > 1 && (
          <button
            type="button"
            className="block-image-nav prev"
            onClick={() => stepImage(-1)}
            title="Previous"
            aria-label="Previous block image"
          />
        )}

        <div className="block-image-panel-stage">
          {loading && (
            <div className="block-image-panel-status">Loading block images…</div>
          )}
          {!loading && current && (
            <BlockImageOsd
              url={current.url}
              mpp={getSectionStarMpp(current.state)}
              syncKey={current.state}
            />
          )}
        </div>

        {!loading && images.length > 1 && (
          <button
            type="button"
            className="block-image-nav next"
            onClick={() => stepImage(1)}
            title="Next"
            aria-label="Next block image"
          />
        )}
      </div>
    </div>
  );
};

BlockImagePanel.propTypes = {
  images: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  selectedIndex: PropTypes.number,
  stepImage: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  sync: PropTypes.object.isRequired,
  setPointMode: PropTypes.func.isRequired,
  adjustPoints: PropTypes.func.isRequired,
  addLandmark: PropTypes.func.isRequired,
  undoLandmark: PropTypes.func.isRequired,
  clearLandmarks: PropTypes.func.isRequired,
  applyLandmarks: PropTypes.func.isRequired,
};

// blockImagesMeta (deviceId / sessionId / blockId) is still intentionally not read
// here — the caption above is built from the current image's own stateLabel/
// typeLabel, not from this. blockImagesMeta stays in the store for anything that
// wants the resolved ids later.
const mapStateToProps = (state) => ({
  // Falls back to a shared empty array: the Viewer slice is persisted, and
  // redux-persist replaces it wholesale on rehydrate, so a session saved before
  // this key existed brings it back undefined — and `images.length` below would
  // throw. Same reason as the sidebar's EMPTY_BLOCK_IMAGE_ROWS.
  images: state.Viewer.blockImages || EMPTY_BLOCK_IMAGES,
  loading: state.Viewer.blockImagesLoading,
  selectedIndex: state.Viewer.selectedBlockImageIndex,
  sync: state.Viewer.blockImageSync || DEFAULT_BLOCK_IMAGE_SYNC,
});

const mapDispatchToProps = (dispatch) => ({
  stepImage: (delta) => dispatch(stepBlockImageAction(delta)),
  close: () => dispatch(closeBlockImageAction()),
  setPointMode: (on) => dispatch(setSyncPointModeAction(on)),
  adjustPoints: () => dispatch(adjustSyncPointsAction()),
  addLandmark: (pane, point) => dispatch(addSyncLandmarkAction(pane, point)),
  undoLandmark: () => dispatch(undoSyncLandmarkAction()),
  clearLandmarks: () => dispatch(clearSyncLandmarksAction()),
  applyLandmarks: () => dispatch(applyLandmarkAlignmentAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BlockImagePanel);
